import { DesktopUpdates } from './components/DesktopUpdates.js';
import { ScannerTasks } from './components/ScannerTasks.js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// Heebo, Hebrew + Latin subsets only — the full package also ships Cyrillic,
// Greek and math faces the app never renders.
import '@fontsource/heebo/hebrew-300.css';
import '@fontsource/heebo/hebrew-400.css';
import '@fontsource/heebo/hebrew-500.css';
import '@fontsource/heebo/hebrew-600.css';
import '@fontsource/heebo/hebrew-700.css';
import '@fontsource/heebo/hebrew-800.css';
import '@fontsource/heebo/latin-300.css';
import '@fontsource/heebo/latin-400.css';
import '@fontsource/heebo/latin-500.css';
import '@fontsource/heebo/latin-600.css';
import '@fontsource/heebo/latin-700.css';
import '@fontsource/heebo/latin-800.css';
import './i18n.js';
import './styles/app.css';
// Loaded last so the ported design system wins specificity ties against the
// legacy stylesheet while screens are migrated.
import './styles/design.css';
import { App } from './App.js';
import { SessionProvider } from './lib/session.js';
import { ToastProvider } from './components/Toast.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { RequestError } from './lib/api.js';
import { loadDarkMode, loadDesignVariant, loadTheme } from './lib/theme.js';

// Accent colour, dark mode and design variant are per-device preferences, so
// they are applied from local storage before the first paint rather than in an
// effect. Dark mode goes on first: the accent tints depend on the mode.
loadDesignVariant();
loadDarkMode();
loadTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Auth and validation failures will not fix themselves on a retry.
        if (error instanceof RequestError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <ToastProvider>
            {/* Outermost catch: a crash in the shell itself, on a public page,
                or on the auth screens has nothing else between it and a blank
                document. Individual pages are caught closer, inside AppShell. */}
            <ErrorBoundary>
              <App />
              <ScannerTasks />
              <DesktopUpdates />
            </ErrorBoundary>
          </ToastProvider>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

/*
 * Dismiss the launch screen in index.html.
 *
 * It waits for the wordmark to finish writing rather than for React alone: the
 * shell mounts well inside the animation, and cutting the stroke half-drawn is
 * the one thing that makes a launch screen look broken. With
 * `prefers-reduced-motion` there is no animation to wait for and this resolves
 * on the same frame.
 */
const splash = document.getElementById('splash');
if (splash) {
  const written = splash.firstElementChild
    ? Promise.all(splash.firstElementChild.getAnimations().map((a) => a.finished))
    : Promise.resolve([]);
  void written.catch(() => []).then(() => {
    splash.classList.add('is-done');
    splash.addEventListener('transitionend', () => splash.remove(), { once: true });
    // A transition that never fires (element hidden, motion reduced) must not
    // leave the overlay swallowing every tap.
    setTimeout(() => splash.remove(), 600);
  });
}
